import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  PortalLoading,
  StatusBadge,
  StudentPageHeader,
  niceDate,
  niceDateTime,
  useStudentPortal,
} from "./studentPortalUtils";

function StudentLearningPage() {
  const { data, loading, reload } = useStudentPortal("student-portal/learning/");
  const [assignmentRows, setAssignmentRows] = useState([]);
  const [submittingId, setSubmittingId] = useState(null);
  const [files, setFiles] = useState({});
  const [comments, setComments] = useState({});

  const loadAssignments = async () => {
    try {
      const response = await axiosInstance.get("student-portal/assignment-submissions/");
      setAssignmentRows(response.data?.assignments || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  const assignmentMap = useMemo(
    () => Object.fromEntries(assignmentRows.map((item) => [String(item.id), item])),
    [assignmentRows]
  );

  if (loading && !data) return <PortalLoading />;

  const resources = data?.resources || [];
  const liveClasses = data?.live_classes || [];

  const submitWork = async (item) => {
    const file = files[item.id];
    if (!file) return toast.warning("Choose the file you want to submit.");

    const payload = new FormData();
    payload.append("submission_file", file);
    payload.append("student_comment", comments[item.id] || "");

    try {
      setSubmittingId(item.id);
      const response = await axiosInstance.post(
        `student-portal/assignments/${item.id}/submission/`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(response.data?.message || "Work submitted.");
      setFiles((current) => ({ ...current, [item.id]: null }));
      setComments((current) => ({ ...current, [item.id]: "" }));
      await loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to submit this work.");
    } finally {
      setSubmittingId(null);
    }
  };

  const renderSubmission = (item) => {
    if (!["HOMEWORK", "ASSIGNMENT"].includes(item.resource_type)) return null;

    const assignment = assignmentMap[String(item.id)] || {};
    const submission = assignment.submission;
    const status = assignment.submission_status || "NOT_SUBMITTED";
    const canSubmit = !submission || status === "RESUBMIT_REQUESTED";

    return (
      <div className="mt-3 border-top pt-3">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <strong>My submission:</strong>
          <StatusBadge value={status} />
          {assignment.overdue && <span className="badge text-bg-danger">OVERDUE</span>}
          {submission?.is_late && <span className="badge text-bg-warning">SUBMITTED LATE</span>}
        </div>

        {submission && (
          <div className="small mb-2">
            <div>Submitted: {niceDateTime(submission.submitted_at)}</div>
            <div>Version: {submission.submission_version}</div>
            {submission.submission_file_url && (
              <a href={submission.submission_file_url} target="_blank" rel="noreferrer">
                Open my submitted file
              </a>
            )}
          </div>
        )}

        {submission?.teacher_feedback && (
          <div className={`alert py-2 ${status === "RESUBMIT_REQUESTED" ? "alert-warning" : "alert-success"}`}>
            <strong>Teacher feedback:</strong> {submission.teacher_feedback}
            {submission.feedback_file_url && (
              <div className="mt-1">
                <a href={submission.feedback_file_url} target="_blank" rel="noreferrer">
                  Open teacher feedback file
                </a>
              </div>
            )}
          </div>
        )}

        {canSubmit ? (
          <div className="row g-2">
            <div className="col-lg-5">
              <input
                type="file"
                className="form-control form-control-sm"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp"
                onChange={(e) =>
                  setFiles((current) => ({
                    ...current,
                    [item.id]: e.target.files?.[0] || null,
                  }))
                }
              />
            </div>
            <div className="col-lg-5">
              <input
                className="form-control form-control-sm"
                placeholder="Optional message to your teacher"
                value={comments[item.id] || ""}
                onChange={(e) =>
                  setComments((current) => ({
                    ...current,
                    [item.id]: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-lg-2">
              <button
                type="button"
                className="btn btn-sm btn-success w-100"
                disabled={submittingId === item.id}
                onClick={() => submitWork(item)}
              >
                {submittingId === item.id
                  ? "Submitting..."
                  : status === "RESUBMIT_REQUESTED"
                    ? "Resubmit"
                    : "Submit Work"}
              </button>
            </div>
          </div>
        ) : (
          <small className="text-muted">
            Your work is already with the teacher. You can upload another file only if a resubmission is requested.
          </small>
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      <StudentPageHeader
        title="My Learning"
        subtitle="Notes, homework, assignments, submissions and live classes."
        icon="bi-play-circle"
        actions={
          <button
            className="btn btn-light border"
            onClick={() => {
              reload();
              loadAssignments();
            }}
          >
            Refresh
          </button>
        }
      />

      <div className="row g-3">
        <div className="col-xl-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Learning Resources & Assigned Work</h5>

              {resources.length === 0 ? (
                <EmptyState title="No resources" />
              ) : (
                resources.map((item) => (
                  <div className="border rounded-3 p-3 mb-3" key={item.id}>
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div>
                        <h6 className="fw-bold mb-1">{item.title}</h6>
                        <small className="text-muted">
                          {item.subject || "General"} · {item.resource_type}
                          {item.due_date ? ` · Due ${niceDate(item.due_date)}` : ""}
                        </small>
                      </div>
                      <StatusBadge value={item.can_view ? item.access_level : "Restricted"} />
                    </div>

                    <p className="small text-muted mt-2 mb-2">
                      {item.description || item.preview_text || "No description."}
                    </p>

                    {item.restricted_message && (
                      <div className="alert alert-warning py-2 mb-2">{item.restricted_message}</div>
                    )}

                    <div className="d-flex flex-wrap gap-2">
                      {item.file_url && (
                        <a className="btn btn-sm btn-success" href={item.file_url} target="_blank" rel="noreferrer">
                          Open Teacher File
                        </a>
                      )}
                      {item.external_url && (
                        <a className="btn btn-sm btn-outline-success" href={item.external_url} target="_blank" rel="noreferrer">
                          Open Link
                        </a>
                      )}
                    </div>

                    {item.can_view && renderSubmission(item)}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Live Classes</h5>
              {liveClasses.length === 0 ? (
                <EmptyState title="No live classes" />
              ) : (
                liveClasses.map((item) => (
                  <div className="border rounded-3 p-3 mb-2" key={item.id}>
                    <h6 className="fw-bold mb-1">{item.title}</h6>
                    <small className="text-muted d-block">
                      {item.subject} · {niceDateTime(item.scheduled_start)}
                    </small>
                    {item.can_join ? (
                      <a className="btn btn-sm btn-success mt-2" href={item.meeting_url} target="_blank" rel="noreferrer">
                        Join / Open
                      </a>
                    ) : (
                      <div className="small text-warning mt-2">Restricted by fee access.</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentLearningPage;
